import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowRight, FaFilter } from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "../BaseURL";
import "../Assets/WebsiteCss/Project.css";

function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/projects`);
      setProjects(res.data.data || []);
    } catch (err) {
      setError("Failed to load projects");
      setProjects([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/category/get-category`);
      const categoriesData = res.data.data || res.data;

      const map = {};
      categoriesData.forEach((cat) => {
        map[cat._id] = cat.categoryName;
      });

      setCategories(categoriesData);
      setCategoryMap(map);
    } catch (err) {
      console.error("Failed to fetch categories");
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchProjects(), fetchCategories()]);
      setLoading(false);
    };
    load();
  }, []);

  // Truncate and strip HTML tags for preview
  const truncateText = (text, max) => {
    if (!text) return "";
    // Remove HTML tags
    const plain = text.replace(/<[^>]+>/g, "");
    return plain.length > max ? plain.substring(0, max) + "..." : plain;
  };

  // Filter projects based on selected category
  const filteredProjects =
    selectedCategory === "all"
      ? projects
      : projects.filter((project) => project.category === selectedCategory);

  // Count projects per category
  const getCategoryCount = (categoryId) => {
    if (categoryId === "all") return projects.length;
    return projects.filter((p) => p.category === categoryId).length;
  };

  return (
    <div className="projects-page">
      <div className="projects-header">
        <h1>Our Projects</h1>
        <p>Here we showcase our completed and ongoing construction projects.</p>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading projects...</p>
        </div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="projects-layout">
          {/* MOBILE FILTER TOGGLE */}
          <button
            className="mobile-filter-toggle"
            onClick={() => setFilterOpen(!filterOpen)}
          >
            <FaFilter /> Filter by Category
          </button>

          {/* LEFT SIDEBAR - CATEGORY FILTER */}
          <aside className={`filter-sidebar ${filterOpen ? "open" : ""}`}>
            <div className="filter-header">
              <h3>Categories</h3>
              <button
                className="close-filter"
                onClick={() => setFilterOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="category-list">
              {/* ALL PROJECTS */}
              <button
                className={`category-item ${
                  selectedCategory === "all" ? "active" : ""
                }`}
                onClick={() => {
                  setSelectedCategory("all");
                  setFilterOpen(false);
                }}
              >
                <span className="category-name">All Projects</span>
                <span className="category-count">
                  {getCategoryCount("all")}
                </span>
              </button>

              {/* CATEGORY ITEMS */}
              {categories.map((category) => (
                <button
                  key={category._id}
                  className={`category-item ${
                    selectedCategory === category._id ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedCategory(category._id);
                    setFilterOpen(false);
                  }}
                >
                  <span className="category-name">{category.categoryName}</span>
                  <span className="category-count">
                    {getCategoryCount(category._id)}
                  </span>
                </button>
              ))}
            </div>
          </aside>

          {/* RIGHT CONTENT - PROJECTS GRID */}
          <div className="projects-content">
            {filteredProjects.length === 0 ? (
              <div className="no-projects">
                <p>No projects found in this category.</p>
              </div>
            ) : (
              <>
                {/* <div className="projects-info">
                  <p className="projects-count">
                    Showing <strong>{filteredProjects.length}</strong>{" "}
                    {filteredProjects.length === 1 ? "project" : "projects"}
                    {selectedCategory !== "all" && (
                      <span className="selected-category">
                        {" "}
                        in "{categoryMap[selectedCategory]}"
                      </span>
                    )}
                  </p>
                </div> */}

                <div className="projects-grid">
                  {filteredProjects.map((project) => (
                    <div
                      key={project._id}
                      className="project-card"
                      onClick={() => navigate(`/projects/${project._id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === "Enter")
                          navigate(`/projects/${project._id}`);
                      }}
                    >
                      <div className="project-card-inner">
                        {/* IMAGE WRAPPER */}
                        <div className="project-image-wrapper">
                          <img
                            src={
                              project.images?.length
                                ? `${BASE_URL}${project.images[0]}`
                                : "https://via.placeholder.com/400x220?text=No+Image"
                            }
                            alt={project.title}
                            loading="lazy"
                          />

                          {/* CATEGORY BADGE */}
                          <span className="project-category-badge">
                            {categoryMap[project.category] || "Unknown"}
                          </span>
                        </div>

                        {/* CONTENT */}
                        <div className="project-info">
                          <h3>{project.title}</h3>

                          <p className="project-description">
                            {truncateText(project.description, 100)}
                          </p>

                          <button
                            className="view-more-btn"
                            aria-label={`View details of ${project.title}`}
                          >
                            View <FaArrowRight className="arrow-icon" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;
